import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CaseCard } from "@/components/radnici/ProcessCaseUI";
import { InvoiceNavigation } from "@/components/nabava/InvoiceNavigation";
import { SiteCostsPanel } from "@/components/nabava/SiteCostsPanel";
import { errorMessage } from "@/lib/api";
import { hasInvoicePermission, invoiceMoney, receivedInvoices, type Invoice } from "@/lib/receivedInvoices";
import "@/components/nabava/ReceivedInvoices.css";
import "@/components/nabava/InvoiceManagement.css";

export function ReceivedInvoiceManagementPage() {
  const { user } = useAuth();
  const allowed = hasInvoicePermission(user) && hasInvoicePermission(user, "odobri_placanje");
  const [rows, setRows] = useState<Invoice[]>([]),
    [loading, setLoading] = useState(true);
  const [error, setError] = useState(""),
    [message, setMessage] = useState("");
  const [returnId, setReturnId] = useState<string | null>(null),
    [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false),
    [reload, setReload] = useState(0);
  const acting = useRef(false);
  useEffect(() => {
    if (!allowed) return;
    const controller = new AbortController();
    receivedInvoices
      .list(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted)
          setRows(data.filter((row) => ["ceka_upravu", "ceka_placanje"].includes(row.stanje)));
      })
      .catch((cause) => {
        if (!controller.signal.aborted) setError(errorMessage(cause, "Računi za odobrenje nisu učitani."));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [allowed, reload]);
  async function act(row: Invoice, returning = false) {
    if (!allowed || acting.current || row.status !== "O" || (returning && !reason.trim())) return;
    acting.current = true;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      if (returning) await receivedInvoices.returnToReview(row, reason);
      else await receivedInvoices.approve(row.id);
      setRows((current) => current.filter((item) => item.id !== row.id));
      setMessage(
        `${row.number}: ${returning ? "Vraćeno Nikolini na provjeru." : "Plaćanje je odobreno. Aplikacija ne izvršava plaćanje."}`,
      );
      setReturnId(null);
      setReason("");
    } catch (cause) {
      setError(errorMessage(cause, "Radnja nije dovršena. Osvježite prikaz prije ponovnog pokušaja."));
    } finally {
      acting.current = false;
      setBusy(false);
    }
  }
  return (
    <div className="invoice-screen space-y-5 py-4">
      <InvoiceNavigation />
      <h1 className="text-2xl font-bold">Uprava</h1>
      {!allowed ? (
        <p role="alert">Nemate ovlast odobrenja plaćanja.</p>
      ) : (
        <>
          {!loading && rows.length > 0 && <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-950">
            <h2 className="font-bold">Čeka tvoje odobrenje plaćanja</h2>
            <p className="text-sm">
              Nikolina je provjerila račune. Odobrenje bilježe Nikolina ili uprava s ovlasti odobrenja plaćanja.
            </p>
          </div>}
          {error && <p role="alert">{error}</p>}
          {message && (
            <p role="status" className="rounded-lg bg-green-50 p-3 text-green-900">
              {message}
            </p>
          )}
          <CaseCard title="Računi za odobrenje">
            <Button
              variant="secondary"
              disabled={loading || busy}
              aria-describedby={loading || busy ? "management-refresh-reason" : undefined}
              onClick={() => {
                setLoading(true);
                setError("");
                setReturnId(null);
                setReason("");
                setReload((value) => value + 1);
              }}
            >
              Osvježi prikaz
            </Button>
            {(loading || busy) && (
              <p id="management-refresh-reason" className="text-sm">
                {busy ? "Spremanje je u tijeku." : "Učitavanje računa…"}
              </p>
            )}
            {!loading && !rows.length && !error && <p>Nema računa koji čekaju odobrenje.</p>}
            {!loading && (
              <ul className="invoice-list" aria-label="Računi za odobrenje">
                {rows.map((row, index) => {
                  const returning = returnId === row.id;
                  const locked = busy
                    ? "Spremanje je u tijeku."
                    : row.status !== "O"
                      ? "Račun više nije osnutak u Minimaxu."
                      : "";
                  const returnReason = locked || (!reason.trim() ? "Upišite razlog vraćanja Nikolini." : "");
                  return (
                    <li className="management-invoice" key={row.id}>
                      <strong>{row.supplier_name}</strong>
                      <span>{row.number}</span>
                      <span>{row.sites?.map((site) => site.naziv).join(", ") || "—"}</span>
                      <strong>
                        {row.currency === "EUR" ? invoiceMoney(row.amount) : `${row.amount} ${row.currency}`}
                      </strong>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant={index === 0 && !returnId ? "primary" : "secondary"}
                          disabled={!!locked || !!returnId}
                          aria-describedby={locked ? `locked-${row.id}` : returnId ? "return-open-reason" : undefined}
                          onClick={() => void act(row)}
                        >
                          Odobri
                        </Button>
                        <Button
                          variant="secondary"
                          disabled={!!locked}
                          aria-describedby={locked ? `locked-${row.id}` : undefined}
                          onClick={() => {
                            setReturnId(row.id);
                            setReason("");
                          }}
                        >
                          Vrati Nikolini
                        </Button>
                      </div>
                      {locked && (
                        <p className="management-full text-sm" id={`locked-${row.id}`}>
                          {locked}
                        </p>
                      )}
                      {returning && (
                        <div className="management-full space-y-3">
                          <p id="return-open-reason" className="text-sm">
                            Dovršite ili otkažite vraćanje Nikolini.
                          </p>
                          <label>
                            Razlog vraćanja Nikolini
                            <textarea
                              maxLength={2000}
                              value={reason}
                              disabled={busy}
                              onChange={(event) => setReason(event.target.value)}
                            />
                          </label>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="primary"
                              disabled={!!returnReason}
                              aria-describedby={returnReason ? "return-reason" : undefined}
                              onClick={() => void act(row, true)}
                            >
                              Potvrdi vraćanje
                            </Button>
                            <Button
                              variant="secondary"
                              disabled={busy}
                              aria-describedby={busy ? `locked-${row.id}` : undefined}
                              onClick={() => {
                                setReturnId(null);
                                setReason("");
                              }}
                            >
                              Odustani od vraćanja
                            </Button>
                          </div>
                          {returnReason && (
                            <p className="text-sm" id="return-reason">
                              {returnReason}
                            </p>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </CaseCard>
          <SiteCostsPanel />
        </>
      )}
    </div>
  );
}
